import clsx from 'clsx/lite'
import { type GatsbyLinkProps, type Link } from 'gatsby'
import React, {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ElementType,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react'

export type ButtonRef = HTMLButtonElement | HTMLAnchorElement

export type ButtonVariant = 'primary' | 'secondary' | 'secondary-alternate'

type ButtonPropsBase = {
  variant?: ButtonVariant
  className?: string
  icon?: ElementType
  size?: 'big' | 'medium' | 'small'
  // todo permet de gérer l'erreur TS sur le children dans les composants, certainement à cause du forwardRef
  children?: any
  disabled?: boolean
}

type InternalLinkProps = {
  component: typeof Link
  to: string
} & Omit<GatsbyLinkProps<any>, 'to'>

type ExternalLinkProps = {
  component: 'a'
  href: string
} & AnchorHTMLAttributes<HTMLAnchorElement>

type ButtonElementProps = {
  component?: 'button'
} & ButtonHTMLAttributes<HTMLButtonElement>

export type ButtonProps = ButtonPropsBase &
  (InternalLinkProps | ExternalLinkProps | ButtonElementProps)

export const Button = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>((props, outerRef) => {
  const {
    component: Component = 'button',
    children,
    className,
    icon: Icon,
    variant = 'primary',
    size = 'medium',
    disabled = false,
    ...otherProps
  } = props

  const innerRef = useRef<ButtonRef>(null)

  useImperativeHandle(outerRef, () => innerRef.current, [])
  return (
    <Component
      // @ts-expect-error
      ref={innerRef}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-full text-center font-medium',
        size === 'medium' && 'px-6 py-3',
        size === 'small' && 'text-075 px-5 py-2',
        variant === 'primary' &&
          'bg-amplification-iconic hover:bg-theme-primary-accent3! text-[#003500]',
        variant === 'secondary' &&
          'border-grey-200 bg-text-inverse border border-solid text-[#10190D]',
        variant === 'secondary-alternate' &&
          'bg-text-inverse text-[#003500] hover:bg-[#003500]/20!',
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
      {...otherProps}
    >
      {Icon && <Icon className="fill-[#003500]" size={16} />}
      {children}
    </Component>
  )
})

export default Button
